import { Component, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MotionConfig } from 'framer-motion';
import { useChat } from './hooks/useChat';
import { useModels } from './hooks/useModels';
import { useOnline } from './hooks/useOnline';
import { useEffortPreference } from './hooks/useEffortPreference';
import { useConversations } from './hooks/useConversations';
import { useConversationSync } from './hooks/useConversationSync';
import { deserializeMessages } from './hooks/conversationCodec';
import { createReportMessage } from './hooks/messageStream';
import { reportsAPI } from './services/api';
import { loadUserContext, sessionStorageOf } from './services/userContext';
import { DOCKED_PANEL_QUERY, MOBILE_QUERY, useMediaQuery } from './hooks/useMediaQuery';
import { stopSpeaking } from './hooks/useSpeech';
import { Header } from './components/Header';
import { Button } from './components/Button';
import { ChatContainer } from './components/ChatContainer';
import { ChatInput } from './components/ChatInput';
import { ConnectivityBanner } from './components/ConnectivityBanner';
import { ModelSelector } from './components/ModelSelector';
import { EffortSelector } from './components/EffortSelector';
import { ConversationSidebar, SIDEBAR_ID } from './components/ConversationSidebar';
import { ToastProvider, useToast } from './components/Toast';
import { ArtifactProvider, useArtifactActions, useArtifactState } from './components/artifacts/ArtifactContext';
import { LiveMapProvider, useLiveMapController } from './components/visuals/map/LiveMapContext.jsx';
import { LiveDocumentProvider, useLiveDocumentController } from './components/documents/LiveDocumentContext.jsx';
import { ArtifactPanel } from './components/artifacts/ArtifactPanel';
import { ThemeProvider } from './hooks/useTheme';
import { ReportBasketProvider, useReportBasket } from './components/report/ReportBasketContext';
import { EmailUserContext } from './components/email/emailUserContext.js';
import { useReportBasketSync } from './components/report/useReportBasketSync.js';
import { basketStorageKey } from './components/report/reportBasket.js';
import { DOCUMENT_FORMATS } from './components/documents/documentFormats.js';
import { retryableLazy } from './components/visuals/retryableLazy.js';
import { FichiersProvider } from './components/fichiers/Lightbox';
import { useLiensVerifies } from './components/fichiers/useLiensVerifies.js';
import { VisualAskContext } from './components/visuals/visualContext.js';
import {
  CentreProvider, CHEMIN_ACTIVATION, useCentre, useNotifications, useTiroirsCentre,
} from './components/notifications/useNotifications.js';
import { useCompteAgents } from './components/agents/useCompteAgents.js';
import { readPref, writePref } from './utils/storage';
import { modelSupportsEffort } from './utils/effortPreference.js';
import { SITUATION_EFFORT, SITUATION_LABEL, SITUATION_QUESTION } from './utils/quickActions.js';
import {
  OFFLINE_SEND_BLOCKED,
  RECONNECTED_NOTICE_MS,
  connectivityBanner,
  interruptedResponseId,
} from './utils/connectivity.js';
import './App.css';

// Tableau de bord qualité (sprint 6, F4) : /admin/qualite, chunk séparé chargé à la
// demande, sans contexte utilisateur ni historique, sans lien depuis l'en-tête
const QualityDashboard = retryableLazy(() => import('./components/admin/QualityDashboard.jsx'));
const isAdminPath = () => typeof window !== 'undefined' && window.location.pathname.startsWith('/admin/qualite');

// Fenêtre d'activation du Web Push (agents, P1, §6bis) : /notifications/activer, ouverte par la
// cloche dans une fenêtre de premier niveau (l'iframe de la plateforme ne peut pas demander la
// permission) ; même principe que /admin/qualite : chunk séparé, sans contexte utilisateur ni
// historique (le jeton d'abonnement arrive de l'iframe par postMessage)
const ActiverNotifications = retryableLazy(() => import('./components/notifications/ActiverNotifications.jsx'));
const isActivationPath = () => typeof window !== 'undefined' && window.location.pathname.startsWith(CHEMIN_ACTIVATION);
const initialRoute = () => (isAdminPath() ? 'admin' : isActivationPath() ? 'activation' : 'app');

/** Échec de chargement du chunk d'une page à part (réseau, nouvelle livraison). */
class AdminBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="admin-boot" role="alert">
        <p>{this.props.message ?? "Le tableau de bord n'a pas pu être chargé."}</p>
        <Button onClick={() => this.setState({ failed: false })}>Réessayer</Button>
      </div>
    );
  }
}

// Panier du rapport composé (sprint 5) : fenêtre chargée à la première ouverture
const ReportDrawer = retryableLazy(() => import('./components/report/ReportDrawer.jsx'));

/** Fenêtre du panier, montée à la première ouverture puis gardée (animation de sortie). */
function ReportDrawerHost({ messages, conversationId, onCompose, streaming, problems }) {
  const { drawerOpen } = useReportBasket();
  const [mounted, setMounted] = useState(false);
  if (drawerOpen && !mounted) setMounted(true);
  if (!mounted) return null;
  return (
    <Suspense fallback={null}>
      <ReportDrawer open={drawerOpen} messages={messages} conversationId={conversationId} onCompose={onCompose} streaming={streaming} problems={problems} />
    </Suspense>
  );
}

// Centre des agents (P1) : tiroirs de la cloche et du bouton « Agents », chargés à la première
// ouverture puis gardés (animation de sortie), comme le panier
const TIROIRS_CENTRE = {
  notifications: retryableLazy(() => import('./components/notifications/NotificationsDrawer.jsx')),
  agents: retryableLazy(() => import('./components/agents/AgentsDrawer.jsx')),
};

/** Chunk d'un tiroir du centre non chargé : rien n'est affiché, l'hôte est prévenu (onEchec). */
class CentreBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    this.props.onEchec();
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

/**
 * Hôte d'un tiroir du centre (nom : 'notifications' ou 'agents'). Échec de chargement (réseau,
 * nouvelle livraison) : tiroir fermé, erreur annoncée, chargement retenté à l'ouverture suivante.
 */
function CentreDrawerHost({ nom, models }) {
  const { tiroir, fermer } = useCentre();
  const toast = useToast();
  const open = tiroir === nom;
  const [mounted, setMounted] = useState(false);
  const [essai, setEssai] = useState(0);
  if (open && !mounted) setMounted(true);
  if (!mounted) return null;
  const Drawer = TIROIRS_CENTRE[nom];
  const echec = () => {
    setMounted(false);
    setEssai((n) => n + 1);
    fermer();
    toast('Le centre n’a pas pu être chargé : vérifiez la connexion, puis réessayez.', { tone: 'error' });
  };
  return (
    <CentreBoundary key={essai} onEchec={echec}>
      <Suspense fallback={null}>
        <Drawer open={open} models={models} />
      </Suspense>
    </CentreBoundary>
  );
}

const SIDEBAR_PREF = 'digitrans_sidebar_open';
// En-tête pendant une coupure du réseau (UI-11)
const OFFLINE_SERVER_STATUS = { connected: false, toolsCount: 0 };

// Contexte de l'utilisateur transmis par la plateforme dans l'URL, lu une seule fois
// au démarrage (l'historique en a besoin dès le premier rendu). SEC-5 : attaché à
// l'entrée d'historique et relu au rechargement, jamais repris par l'utilisateur suivant
// de l'onglet (services/userContext.js) ; les paramètres sensibles, password compris,
// quittent aussitôt l'URL visible (journaux nginx, Referer, historique).
// Mémorisé pour le chargement : en développement, StrictMode appelle deux fois
// l'initialiseur, et le second appel trouverait l'URL déjà nettoyée.
let startupContext;
function readUserContext() {
  if (startupContext === undefined) {
    startupContext = loadUserContext({
      location: window.location,
      history: window.history,
      storage: sessionStorageOf(window),
    });
  }
  return startupContext;
}

function AppShell() {
  const [userContext] = useState(readUserContext);
  const toast = useToast();
  const { close: closeArtifact, open: openArtifact } = useArtifactActions();
  const { artifact: openedArtifact } = useArtifactState();
  const panelDocked = useMediaQuery(DOCKED_PANEL_QUERY);
  // Carte live (03/11) : état de la carte envoyé avec chaque question (lu au moment de l'envoi)
  const carteLiveRef = useRef(null);
  const getCarteLive = useCallback((question) => carteLiveRef.current?.(question) ?? null, []);

  // Modèles et disponibilité (contrat v1.6) : relus périodiquement, et aussitôt après une
  // erreur MODEL_UNAVAILABLE ; un modèle indisponible ne peut pas être choisi. Modèle par
  // défaut dynamique : sans choix explicite (valable pour la session), la sélection suit le
  // défaut du serveur (Qwen tant qu'il est disponible) ; chaque bascule automatique est
  // annoncée (toast, région lue par les lecteurs d'écran)
  const announceModel = useCallback(
    ({ text, tone }) => toast(text, { tone, duration: tone === 'warning' ? 6000 : 4000 }),
    [toast]
  );
  const { models, selectedModel, selectModel, refreshModels } = useModels({ onNotice: announceModel });
  const history = useConversations(userContext);

  // Profondeur de réflexion (sprint 6, LLM-10) : préférence par utilisateur ; un modèle
  // sans effort (models[].effort === false) n'en reçoit pas (sélecteur inactif)
  const { effort, setEffort } = useEffortPreference(userContext);
  const effortSupported = modelSupportsEffort(selectedModel);

  const {
    messages,
    isLoading,
    error,
    tools,
    serverStatus,
    sendMessage,
    regenerate,
    retryInterrupted,
    editMessage,
    loadMessages,
    getSnapshot,
    rateMessage,
    appendLocalMessage,
    updateLocalMessage,
    clearChat,
    stopGeneration,
    checkServerHealth,
  } = useChat(userContext, selectedModel, {
    ensureConversation: history.enabled ? history.ensureActive : null,
    effort: effortSupported ? effort : null,
    onModelUnavailable: refreshModels,
    getCarteLive,
  });

  // Carte live de la conversation : couches et versions déduites des messages, positions en
  // direct, élément sélectionné, ouverture automatique pendant une réponse qui produit une carte
  const liveMap = useLiveMapController({
    messages,
    userContext,
    appendLocalMessage,
    openArtifact,
    openId: openedArtifact?.id ?? null,
    wide: panelDocked,
    streaming: isLoading,
  });
  carteLiveRef.current = liveMap.resume;
  // Documents vivants de la conversation : versions déduites des messages, section sélectionnée
  const liveDocument = useLiveDocumentController({
    messages,
    openArtifact,
    openId: openedArtifact?.id ?? null,
    wide: panelDocked,
    streaming: isLoading,
  });

  // Liens vérifiés de fichiers de la conversation (photos de véhicules, contrat v1.13)
  const liensVerifies = useLiensVerifies(messages);

  // ── Centre des agents (P1) : notifications en temps réel (flux propre, hors du chat), tiroirs
  // de la cloche et du bouton « Agents » ; inactifs sans contexte complet (hors plateforme) ──
  const tiroirs = useTiroirsCentre();
  const ouvrirTiroir = tiroirs.ouvrir;
  const tiroirRef = useRef(null);
  tiroirRef.current = tiroirs.tiroir;
  // Alerte critique arrivée en direct : toast (annoncé tout de suite) avec « Voir », qui ouvre le
  // tiroir de la cloche, sauf si ce tiroir est déjà ouvert (il l'annonce lui-même)
  const signalerCritique = useCallback((notification) => {
    if (tiroirRef.current === 'notifications') return;
    const voir = { label: 'Voir', onClick: () => ouvrirTiroir('notifications', document.querySelector('[data-centre="cloche"]')) };
    toast(`Alerte critique : ${notification.titre}`, { tone: 'error', action: voir });
  }, [toast, ouvrirTiroir]);
  const notifications = useNotifications(userContext, { onCritique: signalerCritique });
  // Pastille du bouton « Agents » : agents actifs, relus sans ouvrir le tiroir
  const agentsActifs = useCompteAgents(userContext, { actif: notifications.actif, tiroir: tiroirs.tiroir });

  const sync = useConversationSync({
    enabled: history.enabled,
    conversationId: history.activeId,
    messages,
    isLoading,
    userContext,
    onSaved: history.upsert,
  });

  // ── Réseau (UI-11) : envoi bloqué hors ligne ; au retour, enregistrements en échec
  // renvoyés aussitôt, listes relues, « Relancer » pour une réponse interrompue ──
  const [reconnected, setReconnected] = useState(false);
  const online = useOnline({
    onReconnect: () => {
      sync.retry();
      if (history.status === 'error') history.refresh();
      refreshModels();
      checkServerHealth();
      // Notifications reçues pendant la coupure : liste relue, flux rouvert
      notifications.relire();
      setReconnected(true);
    },
  });
  const onlineRef = useRef(online);
  onlineRef.current = online;
  // Hors ligne : l'action est refusée et l'utilisateur prévenu (renvoie true si bloquée)
  const blockedOffline = useCallback(() => {
    if (onlineRef.current) return false;
    toast(OFFLINE_SEND_BLOCKED, { tone: 'error' });
    return true;
  }, [toast]);
  // Question posée avec l'élément sélectionné sur la carte live (puce du compositeur), puis oubliée
  const { selection: carteSelection, selectionner: carteSelectionner } = liveMap;
  const { selection: documentSelection, selectionner: documentSelectionner } = liveDocument;
  const send = useCallback((content, options = {}) => {
    if (blockedOffline()) return false;
    const envoi = sendMessage(content, {
      ...options,
      ...(carteSelection ? { selection: carteSelection } : {}),
      ...(documentSelection ? { documentFocus: { document_id: documentSelection.document_id, section: documentSelection.section } } : {}),
    });
    if (carteSelection) carteSelectionner(null);
    if (documentSelection) documentSelectionner(null);
    return envoi;
  }, [blockedOffline, sendMessage, carteSelection, carteSelectionner, documentSelection, documentSelectionner]);
  const rerun = useCallback((id) => (blockedOffline() ? false : regenerate(id)), [blockedOffline, regenerate]);
  const edit = useCallback((id, text) => (blockedOffline() ? false : editMessage(id, text)), [blockedOffline, editMessage]);
  // Phrase posée dans la conversation par le centre des agents et la fiche d'un agent (« Créer
  // depuis un modèle », « Modifier ») : même forme que VisualAskContext (ChatContainer) ; la
  // carte de confirmation reste obligatoire (D4), rien n'est créé sans clic
  const demande = useMemo(() => ({ ask: send, busy: isLoading }), [send, isLoading]);

  // Point de situation (CU-105) : question en langage naturel, niveau « approfondi » forcé
  // pour cette question seulement (la préférence mémorisée ne change pas)
  const askSituation = useCallback(
    () => send(SITUATION_QUESTION, { effort: SITUATION_EFFORT, action: SITUATION_LABEL }),
    [send]
  );

  // Bandeau : hors ligne, puis « Relancer » tant que la réponse interrompue est la dernière
  const canResume = online && reconnected && !isLoading && interruptedResponseId(messages) !== null;
  const bannerState = connectivityBanner({ online, reconnected, canResume });
  // « Connexion rétablie » sans réponse à relancer : affiché quelques secondes seulement
  useEffect(() => {
    if (!reconnected || canResume) return undefined;
    const timer = setTimeout(() => setReconnected(false), RECONNECTED_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [reconnected, canResume]);
  const resumeInterrupted = useCallback(() => {
    if (blockedOffline()) return;
    setReconnected(false);
    retryInterrupted();
  }, [blockedOffline, retryInterrupted]);

  // ── Barre latérale : colonne mémorisée sur ordinateur, tiroir fermé par défaut sur mobile ──
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const [desktopOpen, setDesktopOpen] = useState(() => readPref(SIDEBAR_PREF, true) !== false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const sidebarOpen = isMobile ? drawerOpen : desktopOpen;

  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setDrawerOpen((open) => !open);
    } else {
      setDesktopOpen((open) => {
        writePref(SIDEBAR_PREF, !open);
        return !open;
      });
    }
  }, [isMobile]);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    requestAnimationFrame(() => document.querySelector(`[aria-controls="${SIDEBAR_ID}"]`)?.focus());
  }, []);

  // Panier du rapport : un par conversation (« local » tant qu'elle n'est pas enregistrée),
  // conservé côté serveur avec la conversation de l'historique (BE-09)
  const [localScope, setLocalScope] = useState(1);
  const basketScope = history.activeId ? `c:${history.activeId}` : `local:${localScope}`;
  const basket = useReportBasketSync({ scope: basketScope, userContext: history.enabled ? userContext : null });
  const forgetBasket = basket.forget;

  /**
   * Rapport composé (DOC-17) : message « système » avec le document en préparation,
   * puis POST /api/reports/compose (assemblage serveur, sans appel au modèle).
   */
  // Éléments refusés au dernier essai (artifact_id → raison), affichés dans le panier
  const [composeProblems, setComposeProblems] = useState({});
  const composeReport = useCallback(async (body) => {
    const { format } = body;
    const id = `report-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const documentId = `${id}:document`;
    // Le serveur relit les visuels dans la conversation ENREGISTRÉE : l'enregistrement en
    // attente (fin de tour + délai de regroupement) part maintenant et on attend sa fin
    const snapshot = getSnapshot();
    const conversationId = body.conversation_id ?? history.getActiveId();
    const saving = conversationId && snapshot.length ? sync.saveAndWait(conversationId, snapshot) : Promise.resolve(true);
    appendLocalMessage(createReportMessage({ id, title: body.title, format, count: body.items.length }));
    const patchDocument = (patch) => updateLocalMessage(id, (m) => ({
      ...m,
      artifacts: (m.artifacts ?? []).map((a) => (a.id === documentId ? { ...a, ...patch } : a)),
    }));
    try {
      await saving;
      const spec = await reportsAPI.compose(body, userContext);
      setComposeProblems({});
      patchDocument({
        status: 'ready',
        spec: { ...spec, origin: 'report' },
        kind: 'document',
        format: spec.format ?? format,
        title: spec.title ?? body.title,
        message: null,
      });
    } catch (err) {
      let reason = err instanceof TypeError ? 'serveur injoignable, vérifiez votre connexion' : err.message;
      // Détail par élément (non enregistré, antérieur au sprint 5, sceau rompu…) : repris
      // dans le message et affiché à côté de l'élément dans le panier
      if (err?.details?.length) {
        setComposeProblems(Object.fromEntries(err.details.map((d) => [d.id, d.raison])));
        reason = `${reason} (${err.details.length} élément${err.details.length > 1 ? 's' : ''} : ${err.details.map((d) => d.raison).join(' ; ')})`;
      }
      patchDocument({ status: 'error', message: `Rapport ${DOCUMENT_FORMATS[format]?.label ?? ''} non généré : ${reason}. Réessayez depuis le panier.` });
      toast('Le rapport n’a pas pu être généré', { tone: 'error' });
    }
  }, [appendLocalMessage, updateLocalMessage, userContext, toast, getSnapshot, history, sync]);

  const [openingId, setOpeningId] = useState(null);
  // Jeton de sélection : incrémenté à chaque sélection, nouvelle conversation ou
  // suppression. Une ouverture dépassée (clic plus récent) est ignorée à son retour.
  const selectSeqRef = useRef(0);

  // Quitter la conversation affichée : la réponse en cours est arrêtée et ce qui a été
  // reçu est enregistré tout de suite ; lecture vocale et panneau d'artefacts fermés.
  // État lu à l'instant de l'appel (refs), jamais celui du rendu du clic : après un
  // await, une closure périmée enregistrerait les messages d'une autre conversation.
  const leaveCurrent = useCallback(() => {
    const currentId = history.getActiveId();
    const snapshot = getSnapshot();
    if (currentId && snapshot.length) sync.saveNow(currentId, snapshot);
    stopGeneration(); // sans effet si aucune réponse n'est en cours
    stopSpeaking();
    closeArtifact();
  }, [history, sync, getSnapshot, stopGeneration, closeArtifact]);

  const startNewConversation = useCallback(() => {
    selectSeqRef.current += 1;
    setOpeningId(null);
    leaveCurrent();
    // Même bloc synchrone : la sauvegarde ne voit jamais l'ancienne conversation vide
    history.startNew();
    clearChat();
    setLocalScope((n) => n + 1);
    if (isMobile) setDrawerOpen(false);
    requestAnimationFrame(() => document.querySelector('.message-input')?.focus());
  }, [leaveCurrent, history, clearChat, isMobile]);

  const selectConversation = useCallback(async (id) => {
    if (id === history.getActiveId()) {
      if (isMobile) closeDrawer();
      return;
    }
    const seq = ++selectSeqRef.current;
    setOpeningId(id);
    try {
      const data = await history.fetchOne(id);
      // Un autre clic (ou « Nouvelle conversation ») est arrivé entre-temps : il prime
      if (seq !== selectSeqRef.current) return;
      const restored = deserializeMessages(data.messages);
      leaveCurrent();
      // Même bloc synchrone : la conversation devient active avec ses propres messages
      sync.markSaved(id, restored);
      loadMessages(restored);
      history.activate(id);
      if (isMobile) setDrawerOpen(false);
    } catch (err) {
      if (seq !== selectSeqRef.current) return;
      toast(err.status === 404 ? "Cette conversation n'existe plus" : "Impossible d'ouvrir la conversation", { tone: 'error' });
      if (err.status === 404) history.refresh();
    } finally {
      // Seulement si cette ouverture est toujours celle affichée en chargement
      setOpeningId((current) => (current === id && seq === selectSeqRef.current ? null : current));
    }
  }, [history, isMobile, closeDrawer, leaveCurrent, sync, loadMessages, toast]);

  const deleteConversation = useCallback(async (id) => {
    await history.remove(id);
    sync.forget(id);
    forgetBasket(id);
    if (id === history.getActiveId()) {
      selectSeqRef.current += 1;
      setOpeningId(null);
      stopGeneration();
      stopSpeaking();
      closeArtifact();
      history.startNew();
      clearChat();
      setLocalScope((n) => n + 1);
    }
    // Ancien panier local de la conversation supprimée (antérieur au panier serveur) : retiré
    writePref(basketStorageKey(id), null);
  }, [history, sync, forgetBasket, stopGeneration, closeArtifact, clearChat]);

  return (
    <LiveMapProvider value={liveMap}>
    <LiveDocumentProvider value={liveDocument}>
    <ReportBasketProvider basket={basket}>
    <EmailUserContext.Provider value={userContext}>
    <CentreProvider notifications={notifications} tiroirs={tiroirs} demande={demande} agentsActifs={agentsActifs}>
    <div className="app">
      {/* Hors ligne : « Déconnecté » jusqu'au retour du réseau (état du serveur relu ensuite) */}
      <Header
        serverStatus={online ? serverStatus : OFFLINE_SERVER_STATUS}
        tools={tools}
        onClearChat={startNewConversation}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={toggleSidebar}
        sidebarId={SIDEBAR_ID}
        historyEnabled={history.enabled}
        showNewButton={isMobile || !sidebarOpen}
      />

      {/* Liens vérifiés de fichiers (photos de véhicules, contrat v1.13) et visionneuse : fil de la
          conversation et panneau d'artefacts (carte en grand, infobulle avec photo) */}
      <FichiersProvider liens={liensVerifies}>
      <div className="app-body">
        <ConversationSidebar
          history={history}
          open={sidebarOpen}
          isMobile={isMobile}
          onClose={closeDrawer}
          onSelect={selectConversation}
          onNew={startNewConversation}
          onDelete={deleteConversation}
          openingId={openingId}
          sync={history.enabled ? sync : null}
        />

        <div className="app-main">
          <ChatContainer
            messages={messages}
            isLoading={isLoading}
            error={error}
            onRate={rateMessage}
            onRegenerate={rerun}
            onEdit={edit}
            onSuggestion={send}
            onSituation={askSituation}
            effortSupported={effortSupported}
            loadingConversation={!!openingId && !messages.length}
          />
          <ChatInput
            onSend={send}
            isLoading={isLoading}
            onStop={stopGeneration}
            onSituation={askSituation}
            showSituation={messages.length > 0}
            effortSupported={effortSupported}
            offline={!online}
            selection={documentSelection ? { nom: documentSelection.section, libelle: `Section « ${documentSelection.section} » sélectionnée dans le document` } : carteSelection}
            onClearSelection={() => (documentSelection ? documentSelectionner(null) : carteSelectionner(null))}
            banner={<ConnectivityBanner state={bannerState} onResume={resumeInterrupted} />}
            effortSelector={
              <EffortSelector value={effort} onChange={setEffort} supported={effortSupported} />
            }
            modelSelector={
              <ModelSelector
                models={models}
                selectedModel={selectedModel}
                onSelect={selectModel}
                disabled={isLoading}
              />
            }
          />
        </div>

        <ArtifactPanel messages={messages} />
      </div>
      </FichiersProvider>
      <ReportDrawerHost messages={messages} conversationId={history.activeId} onCompose={composeReport} streaming={isLoading} problems={composeProblems} />
      {/* Tiroirs du centre (P1), hors du fil : la phrase d'un modèle part dans la conversation
          par VisualAskContext, fourni ici comme dans ChatContainer */}
      <VisualAskContext.Provider value={demande}>
        <CentreDrawerHost nom="notifications" models={models} />
        <CentreDrawerHost nom="agents" models={models} />
      </VisualAskContext.Provider>
    </div>
    </CentreProvider>
    </EmailUserContext.Provider>
    </ReportBasketProvider>
    </LiveDocumentProvider>
    </LiveMapProvider>
  );
}

function App() {
  const [route] = useState(initialRoute);
  return (
    // reducedMotion="user" : framer-motion coupe les animations si l'utilisateur les réduit
    <ThemeProvider>
      <MotionConfig reducedMotion="user">
        {route === 'admin' ? (
          <AdminBoundary>
            <Suspense fallback={<div className="admin-boot" role="status">Chargement du tableau de bord…</div>}>
              <QualityDashboard />
            </Suspense>
          </AdminBoundary>
        ) : route === 'activation' ? (
          <AdminBoundary message="La page d’activation des notifications n’a pas pu être chargée.">
            <Suspense fallback={<div className="admin-boot" role="status">Chargement…</div>}>
              <ActiverNotifications />
            </Suspense>
          </AdminBoundary>
        ) : (
          <ToastProvider>
            <ArtifactProvider>
              <AppShell />
            </ArtifactProvider>
          </ToastProvider>
        )}
      </MotionConfig>
    </ThemeProvider>
  );
}

export default App;
